/**
 * Contextual Help Button
 * Small "?" icon that links to the relevant help guide for the current page.
 */

import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import { HelpOutlined as HelpIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { canRoleReadGuide, getGuideBySlug } from "@/pages/help/guides";

interface HelpButtonProps {
  /** Guide slug to link to (e.g., "create-campus") */
  slug: string;
  /** Optional tooltip text override */
  tooltip?: string;
  /** Size variant */
  size?: "small" | "medium";
}

export const HelpButton: React.FC<HelpButtonProps> = ({
  slug,
  tooltip = "View guide",
  size = "small",
}) => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const guide = getGuideBySlug(slug);

  // Only offer a guide the reader can open: a teacher on a shared page must
  // not be sent to an office-only guide. An unknown slug still renders (it
  // opens the "Guide not found" page) so a typo is noticed, not hidden.
  if (!guide) {
    if (import.meta.env.DEV) console.warn(`[help] HelpButton: no guide "${slug}"`);
  } else if (!canRoleReadGuide(guide, role)) {
    return null;
  }

  return (
    <Tooltip title={tooltip} arrow>
      <IconButton
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/help/${slug}`);
        }}
        sx={{
          color: "text.secondary",
          border: "1px solid",
          borderColor: "grey.300",
          width: size === "small" ? 28 : 34,
          height: size === "small" ? 28 : 34,
          "&:hover": {
            color: "primary.main",
            borderColor: "primary.main",
            bgcolor: "primary.50",
          },
        }}
      >
        <HelpIcon sx={{ fontSize: size === "small" ? 16 : 20 }} />
      </IconButton>
    </Tooltip>
  );
};

export default HelpButton;
